<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <ItSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <ItTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header & Actions -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage system access and user roles</p>
          </div>
          <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <!-- Search Input -->
            <div class="relative w-full sm:w-72">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input 
                v-model="search" 
                type="text" 
                placeholder="Search users..." 
                class="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition-shadow"
              />
            </div>
            <!-- Add User Button -->
            <button 
              @click="openModal()" 
              class="inline-flex items-center justify-center px-5 py-2.5 bg-[#74bd54] hover:bg-[#63a43f] text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          </div>
        </div>

        <!-- Users Grid -->
        <div v-if="filteredUsers.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div 
            v-for="user in filteredUsers" 
            :key="user.id" 
            class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600 transition-all group"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center space-x-4">
                <!-- Avatar -->
                <div class="w-12 h-12 rounded-full bg-gradient-to-br from-[#035a76] to-[#04698f] flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0">
                  {{ user.initials }}
                </div>
                <div class="min-w-0">
                  <h3 class="font-semibold text-gray-900 dark:text-white truncate" :title="user.name">{{ user.name }}</h3>
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 mt-1">
                    {{ user.role }}
                  </span>
                </div>
              </div>
            </div>
            
            <!-- Email -->
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-4 flex items-center gap-2 truncate" :title="user.email">
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {{ user.email }}
            </p>

            <!-- Actions -->
            <div class="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-3">
              <button 
                v-if="hasPermission('Users', 'edit_user')" 
                @click="editUser(user)" 
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
              >
                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button 
                v-if="hasPermission('Users', 'delete_user')" 
                @click="confirmDelete(user.id)" 
                class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
          <svg class="h-16 w-16 mb-4 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p class="text-lg font-medium">No users found</p>
          <p class="text-sm mt-1">Try adjusting your search or add a new user.</p>
        </div>

        <!-- Add/Edit User Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="closeModal">
            <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              
              <!-- Modal Header -->
              <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ editing ? "Edit User" : "Add New User" }}
                </h3>
                <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Modal Body -->
              <form @submit.prevent="saveUser" class="p-6 space-y-5">
                
                <!-- Select Staff Member -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Select Staff Member <span class="text-red-500">*</span>
                  </label>
                  <Multiselect
                    v-model="selectedStaff"
                    :options="staffMembers"
                    label="applicant_name"
                    track-by="id"
                    placeholder="Search and select staff..."
                    :searchable="true"
                    :close-on-select="true"
                    class="multiselect-custom"
                  />
                </div>

                <!-- Full Name (Auto-filled) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Full Name</label>
                  <input
                    v-model="form.name"
                    type="text"
                    readonly
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 px-3 py-2.5 cursor-not-allowed"
                  />
                </div>

                <!-- New Email -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address <span class="text-red-500">*</span></label>
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="user@company.com"
                    required
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition"
                  />
                </div>

                <!-- Password -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Password <span v-if="!editing" class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.password"
                    type="password"
                    :placeholder="editing ? 'Leave blank to keep current' : 'Enter password'"
                    :required="!editing"
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition"
                  />
                </div>

                <!-- Role (Department Name) -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Role (Department)</label>
                  <input
                    v-model="form.role"
                    type="text"
                    readonly
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 px-3 py-2.5 cursor-not-allowed"
                  />
                </div>

                <!-- Modal Footer -->
                <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    type="button" 
                    @click="closeModal" 
                    class="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    :disabled="isLoading"
                    class="inline-flex items-center px-5 py-2.5 bg-[#74bd54] hover:bg-[#63a43f] text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors min-w-[100px] justify-center"
                  >
                    <svg v-if="isLoading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ isLoading ? 'Saving...' : 'Save User' }}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Transition>

      </main>
    </div>
  </div>

  <!-- Confirm Delete Modal -->
  <ConfirmModal
    :show="showDeleteModal"
    title="Delete User"
    message="Are you sure you want to delete this user? This action cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    @confirm="handleDelete"
    @cancel="showDeleteModal = false"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import api from "@/services/api";
import ConfirmModal from "@/components/hr/ConfirmModal.vue";
import { useToast } from 'vue-toastification';
import { usePermissions } from '@/composables/usePermissions';
import ItTopbar from "../../components/it/ItTopbar.vue";
import ItSidebar from "../../components/it/ItSidebar.vue";

const { hasPermission } = usePermissions();
const toast = useToast();
const isLoading = ref(false);

const currentPageTitle = ref("Users");
const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");
const users = ref([]);
const staffMembers = ref([]);
const selectedStaff = ref(null);

const showDeleteModal = ref(false);
const itemToDelete = ref(null);

const form = ref({
  id: null,
  applicant_id: null,
  name: "",
  email: "",
  password: "",
  role: ""
});

// Fetch Staff
const fetchStaffMembers = async () => {
  try {
    const response = await api.get('get_hired_applicants.php');
    staffMembers.value = response.data.data.map(item => ({
      id: item.applicant_id,
      applicant_name: item.applicant_name,
      department_name: item.department?.department_name || 'Staff'
    }));
  } catch (error) {
    console.error('Error fetching staff:', error);
    toast.error("Failed to load staff list");
  }
};

// Watch for selection change
watch(selectedStaff, (newVal) => {
  if (newVal) {
    form.value.applicant_id = newVal.id;
    form.value.name = newVal.applicant_name || '';
    form.value.role = newVal.department_name || 'Staff';
  }
});

// Fetch Users
const fetchUsers = async () => {
  try {
    const response = await api.get("Users.php");
    users.value = response.data
      .filter(user => Number(user.applicant_id) !== 0)
      .map(user => ({
        id: user.id,
        name: user.full_name,
        email: user.email,
        role: user.user_type || user.role,
        applicant_id: user.applicant_id,
        initials: getInitials(user.full_name)
      }));
  } catch (error) {
    console.error("Error fetching users:", error);
    toast.error("Failed to load users");
  }
};

const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || '';

const filteredUsers = computed(() => users.value.filter(u => 
  (u.name || '').toLowerCase().includes(search.value.toLowerCase()) ||
  (u.email || '').toLowerCase().includes(search.value.toLowerCase()) ||
  (u.role || '').toLowerCase().includes(search.value.toLowerCase())
));

const openModal = () => {
  editing.value = false;
  selectedStaff.value = null;
  form.value = { id: null, applicant_id: null, name: "", email: "", password: "", role: "" };
  showModal.value = true;
};

const editUser = (user) => {
  editing.value = true;
  form.value = { ...user, password: "" }; // Clear password for security
  showModal.value = true;
};

const saveUser = async () => {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const payload = { ...form.value };
    let response;

    if (editing.value) {
      // Remove password from payload if empty during edit
      if (!payload.password) delete payload.password;
      response = await api.put("Users.php", payload);
    } else {
      response = await api.post("Users.php", payload);
    }

    toast.success(editing.value ? "User updated successfully!" : "User created successfully!");
    closeModal();
    fetchUsers();
  } catch (error) {
    toast.error(error.response?.data?.message || error.message || "Failed to save");
  } finally {
    isLoading.value = false;
  }
};

const confirmDelete = (id) => { 
  itemToDelete.value = id; 
  showDeleteModal.value = true; 
};

const handleDelete = async () => {
  try {
    await api.delete("Users.php", { data: { id: itemToDelete.value } });
    users.value = users.value.filter(u => u.id !== itemToDelete.value);
    toast.success("User deleted successfully!");
  } catch (err) {
    toast.error("Failed to delete user.");
  } finally {
    showDeleteModal.value = false;
  }
};

const closeModal = () => {
  showModal.value = false;
  selectedStaff.value = null;
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark");
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove("dark");
    localStorage.theme = 'light';
  }
};

onMounted(() => {
  fetchUsers();
  fetchStaffMembers();
});
</script>

<style scoped>
/* Modal Transitions */
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.95); }

/* Vue-Multiselect Dark Mode Overrides */
:deep(.multiselect__tags) {
  @apply bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg min-h-[42px] py-2 px-3;
}
:deep(.multiselect__input) {
  @apply bg-transparent dark:bg-gray-700 text-gray-900 dark:text-white;
}
:deep(.multiselect__content-wrapper) {
  @apply bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600;
}
:deep(.multiselect__option) {
  @apply text-gray-900 dark:text-white;
}
:deep(.multiselect__option--highlight) {
  @apply bg-[#74bd54] text-white;
}
:deep(.multiselect__option--selected) {
  @apply bg-[#74bd54]/10 text-[#74bd54] dark:bg-[#74bd54]/30 dark:text-green-300;
}
:deep(.multiselect__placeholder) {
  @apply text-gray-400 dark:text-gray-500;
}
</style>